/**
 * 软键盘 inset hook（任务 10d）：订阅 visualViewport resize/scroll，返回键盘
 * 占用高度 px（口径见 composerModel.keyboardInset）。仅移动布局的聊天页使用；
 * 不支持 visualViewport 的环境（旧 WebView）恒 0——键盘行为退回浏览器默认。
 *
 * 21ah-3（人工实拍 Web Clip 点输入框）：
 * - **量聊天页根元素的 rect 底边**，不再拿 `document.body.clientHeight` 当基准。
 *   Web Clip 下键盘弹出时 iOS 会把整页往上滚一截（页头都滚出了屏幕），用高度
 *   当基准等于把那一截重复计一遍 → 输入栏顶过头，和键盘之间空出一条白带。
 * - **顺带把这一截滚回去**：外壳是 `height:100%` 不可滚的，`scrollY>0` 一定是
 *   iOS 自己为了「露出聚焦元素」滚的，复位后页头回来、几何也回到常态。
 */
import { useEffect, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { keyboardInset } from './composerModel';

export function useKeyboardInset(
  enabled: boolean,
  pageRef: RefObject<HTMLElement | null>,
): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setInset(0);
      return undefined;
    }
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = (): void => {
      // iOS 偷滚复位（见文件头）：先复位再量，否则量到的是滚过的 rect
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      const el = pageRef.current;
      if (el === null) return;
      setInset(keyboardInset(el.getBoundingClientRect().bottom, vv.height, vv.offsetTop));
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled, pageRef]);

  return enabled ? inset : 0;
}
